/**
 * Region Configuration for Branon+ (Grades 11-12, Exam Prep)
 *
 * Controls all region-specific behaviour: features, curriculum, locale, exam prep.
 * Adding a new country = adding a new config block here + .env file.
 *
 * Detection priority:
 *   1. User profile region (future - from Supabase user metadata)
 *   2. VITE_REGION env var
 *   3. Default: "IN"
 */

// ── Types ──────────────────────────────────────────────────────

export interface RegionFeatures {
  showRealLifeExamples: boolean;
  showChatbot: boolean;
  showWriting: boolean;
  showBadges: boolean;
  showExamPrep: boolean;
  showLegacyConceptSections: boolean;
  showExplorePanel: boolean;
}

export interface RegionCurriculum {
  boardOptions: string[];
  gradeRange: { min: number; max: number };
  availableExams: string[];
}

export interface RegionLocale {
  currency: string;
  currencySymbol: string;
  names: string[];
  places: string[];
  timezone: string;
  spellingStyle: string;
}

export type QuestionType = 'mcq_single' | 'mcq_multi' | 'numerical' | 'assertion_reasoning' | 'short_answer' | 'long_answer' | 'case_based';

export interface SectionBlueprint {
  label: string;
  title: string;
  questionType: QuestionType;
  count: number;
  marksPerQuestion: number;
  negativeMarks: number;
  attemptLimit?: number;   // for "attempt N of M" sections (e.g. JEE/NEET Section B)
}

export interface PaperBlueprint {
  durationMinutes: number;
  totalMarks: number;
  sections: SectionBlueprint[];
  readingTimeMinutes?: number;  // silent reading window before exam timer starts (e.g. CBSE 15 min)
}

export interface ExamConfig {
  name: string;
  description: string;
  grades: number[];
  subjects: string[];
  boards?: string[];                                    // school boards that see this exam; undefined = universal
  blueprint: PaperBlueprint;                           // default blueprint (or R&W blueprint for SAT)
  subjectBlueprints?: Record<string, PaperBlueprint>;  // per-subject override (SAT) or per-state (State Board)
  breakBetweenSubjectsMinutes?: number;                // mandatory break between subject sessions (SAT: 10 min)
  /**
   * Set when `subjects` are NOT independent standalone papers but parts of one real-world
   * sitting the student cannot opt out of individual subjects for. `subjects` here is a list
   * of covered subjects, not a menu of choices — the UI must not let the student pick just one.
   *   'segmented' — subjects run one after another, each under its own timer, with a mandatory
   *                 break in between (real SAT: R&W modules -> break -> Math modules).
   *   'pooled'    — all subjects' questions are merged into one paper under a single timer with
   *                 free navigation across subjects, no break (real JEE Main/Advanced/NEET).
   */
  continuousFlow?: 'segmented' | 'pooled';
}

export interface RegionConfig {
  region: string;
  countryCode: string;
  configVersion: number;
  features: RegionFeatures;
  curriculum: RegionCurriculum;
  locale: RegionLocale;
  chatbot: {
    botName: string;
    maxDoubtsPerSession: number;
  };
  practiceTest: {
    exams: ExamConfig[];
  };
}

// ── Region Configs ─────────────────────────────────────────────

const REGION_CONFIGS: Record<string, RegionConfig> = {
  IN: {
    region: 'IN',
    countryCode: 'IN',
    configVersion: 2,
    features: {
      showRealLifeExamples: true,
      showChatbot: true,
      showWriting: true,
      showBadges: true,
      showExamPrep: true,
      showLegacyConceptSections: false,
      showExplorePanel: true,
    },
    curriculum: {
      boardOptions: ['CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge'],
      gradeRange: { min: 11, max: 12 },
      availableExams: ['JEE', 'JEE Advanced', 'NEET', 'BOARD'],
    },
    locale: {
      currency: 'INR',
      currencySymbol: '₹',
      names: ['Rahul', 'Priya', 'Amit', 'Sneha', 'Vikram'],
      places: ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata'],
      timezone: 'Asia/Kolkata',
      spellingStyle: 'en-IN',
    },
    chatbot: {
      botName: 'Chinni',
      maxDoubtsPerSession: 5,
    },
    practiceTest: {
      exams: [
        {
          name: 'JEE Main',
          description: 'Joint Entrance Examination — per subject: 30Q / 100 marks / 60 min (full paper: 3 subjects × 100 marks = 300 marks / 3 hrs, one continuous sitting, free navigation across subjects)',
          grades: [11, 12],
          subjects: ['Physics', 'Chemistry', 'Mathematics'],
          continuousFlow: 'pooled',
          // Universal — all boards see this
          blueprint: {
            durationMinutes: 60,
            totalMarks: 100,
            sections: [
              { label: 'Section A', title: 'MCQ (Single Correct)', questionType: 'mcq_single', count: 20, marksPerQuestion: 4, negativeMarks: 1 },
              { label: 'Section B', title: 'Numerical Value (Attempt any 5 of 10)', questionType: 'numerical', count: 10, marksPerQuestion: 4, negativeMarks: 0, attemptLimit: 5 },
            ],
          },
        },
        {
          name: 'NEET',
          description: 'National Eligibility cum Entrance Test (2024 format) — per subject: 50Q / attempt 45 / 180 marks / 60 min (one continuous sitting across all 4 subjects, free navigation)',
          grades: [11, 12],
          subjects: ['Physics', 'Chemistry', 'Botany', 'Zoology'],
          continuousFlow: 'pooled',
          // Universal — all boards see this
          blueprint: {
            durationMinutes: 60,
            totalMarks: 180,
            sections: [
              { label: 'Section A', title: 'MCQ (All Compulsory — 35 Questions)', questionType: 'mcq_single', count: 35, marksPerQuestion: 4, negativeMarks: 1 },
              { label: 'Section B', title: 'MCQ (Attempt any 10 of 15)', questionType: 'mcq_single', count: 15, marksPerQuestion: 4, negativeMarks: 1, attemptLimit: 10 },
            ],
          },
        },
        {
          name: 'JEE Advanced',
          description: 'IIT JEE Advanced — per subject: 13Q / 48 marks / 60 min (full Paper 1: 3 subjects = 3 hrs total, one continuous sitting, free navigation; Paper 2 is a separate sitting)',
          grades: [11, 12],
          subjects: ['Physics', 'Chemistry', 'Mathematics'],
          continuousFlow: 'pooled',
          blueprint: {
            durationMinutes: 60,
            totalMarks: 48,
            sections: [
              { label: 'Section 1', title: 'Single Correct MCQ (+3/−1)', questionType: 'mcq_single', count: 4, marksPerQuestion: 3, negativeMarks: 1 },
              { label: 'Section 2', title: 'One or More Correct MCQ (+4/−2)', questionType: 'mcq_multi', count: 3, marksPerQuestion: 4, negativeMarks: 2 },
              { label: 'Section 3', title: 'Numerical Value (+4/0)', questionType: 'numerical', count: 6, marksPerQuestion: 4, negativeMarks: 0 },
            ],
          },
        },
        {
          name: 'CBSE Board',
          description: 'CBSE Class 11-12 Board Exams — 70 marks / 3 hrs + 15 min reading time',
          grades: [11, 12],
          subjects: ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Economics', 'Business Studies', 'Accountancy'],
          boards: ['CBSE'],
          blueprint: {
            durationMinutes: 180,
            totalMarks: 70,
            readingTimeMinutes: 15,
            sections: [
              { label: 'Section A', title: 'Multiple Choice Questions', questionType: 'mcq_single', count: 12, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section A', title: 'Assertion & Reason', questionType: 'assertion_reasoning', count: 4, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Very Short Answer (2 marks)', questionType: 'short_answer', count: 5, marksPerQuestion: 2, negativeMarks: 0 },
              { label: 'Section C', title: 'Short Answer (3 marks)', questionType: 'short_answer', count: 7, marksPerQuestion: 3, negativeMarks: 0 },
              { label: 'Section D', title: 'Case-Based Questions (4 marks)', questionType: 'case_based', count: 2, marksPerQuestion: 4, negativeMarks: 0 },
              { label: 'Section E', title: 'Long Answer (5 marks)', questionType: 'long_answer', count: 3, marksPerQuestion: 5, negativeMarks: 0 },
            ],
          },
        },
        {
          name: 'ISC Board',
          description: 'ISC Class 11-12 Board Exams — 70 marks / 3 hrs',
          grades: [11, 12],
          subjects: ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Economics', 'Business Studies', 'Accountancy'],
          boards: ['ICSE'],
          blueprint: {
            durationMinutes: 180,
            totalMarks: 70,
            sections: [
              { label: 'Section A', title: 'Short Answer (Compulsory)', questionType: 'short_answer', count: 10, marksPerQuestion: 2, negativeMarks: 0 },
              { label: 'Section B', title: 'Structured Problems', questionType: 'short_answer', count: 5, marksPerQuestion: 4, negativeMarks: 0 },
              { label: 'Section C', title: 'Long Answer', questionType: 'long_answer', count: 5, marksPerQuestion: 6, negativeMarks: 0 },
            ],
          },
        },
        {
          // Renamed from 'State Board' to disambiguate from rewise-ui's
          // 'State Board 10' exam_type — the two apps' exams have different
          // grade semantics and previously collided under one shared
          // server-side identifier.
          name: 'State Board 12',
          description: 'State Board Class 11-12 Exams — format varies by state (70 marks / 3 hrs typical)',
          grades: [11, 12],
          subjects: ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Economics', 'Business Studies', 'Accountancy'],
          boards: ['State Board'],
          // Generic fallback blueprint (used when student state is unknown)
          blueprint: {
            durationMinutes: 180,
            totalMarks: 70,
            sections: [
              { label: 'Section A', title: 'Objective Questions', questionType: 'mcq_single', count: 20, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Short Answer (2 marks)', questionType: 'short_answer', count: 10, marksPerQuestion: 2, negativeMarks: 0 },
              { label: 'Section C', title: 'Long Answer (5 marks)', questionType: 'long_answer', count: 6, marksPerQuestion: 5, negativeMarks: 0 },
            ],
          },
          // Per-state blueprints — keyed by state name (used via getExamBlueprint('State Board 12', stateName)).
          // Note: the server independently re-derives and resolves the authoritative blueprint
          // itself (generate-practice-questions/index.ts STATE_BOARD_BLUEPRINTS) from the
          // student's real state_board — this copy is for on-screen preview rendering only.
          subjectBlueprints: {
            'Maharashtra': {
              // HSC: 70m theory + 30m practicals; Section A mixed MCQ+VSA, then 3 descriptive sections
              durationMinutes: 180, totalMarks: 70,
              sections: [
                { label: 'Section A', title: 'MCQ (1 mark)', questionType: 'mcq_single', count: 10, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Section A', title: 'Very Short Answer (1 mark)', questionType: 'short_answer', count: 8, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Section B', title: 'Short Answer I (2 marks)', questionType: 'short_answer', count: 12, marksPerQuestion: 2, negativeMarks: 0, attemptLimit: 8 },
                { label: 'Section C', title: 'Short Answer II (3 marks)', questionType: 'short_answer', count: 12, marksPerQuestion: 3, negativeMarks: 0, attemptLimit: 8 },
                { label: 'Section D', title: 'Long Answer (4 marks)', questionType: 'long_answer', count: 5, marksPerQuestion: 4, negativeMarks: 0, attemptLimit: 3 },
              ],
            },
            'Tamil Nadu': {
              // HSC: 70m theory + 20m practicals + 10m internal; 4-part structure
              durationMinutes: 180, totalMarks: 70,
              sections: [
                { label: 'Part I', title: 'MCQ (1 mark)', questionType: 'mcq_single', count: 15, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Part II', title: 'Short Answer (2 marks)', questionType: 'short_answer', count: 9, marksPerQuestion: 2, negativeMarks: 0, attemptLimit: 6 },
                { label: 'Part III', title: 'Short Answer (3 marks)', questionType: 'short_answer', count: 9, marksPerQuestion: 3, negativeMarks: 0, attemptLimit: 6 },
                { label: 'Part IV', title: 'Essay (5 marks)', questionType: 'long_answer', count: 5, marksPerQuestion: 5, negativeMarks: 0 },
              ],
            },
            'Karnataka': {
              // 2nd PUC: 70m theory + 30m practicals; 5-part structure including numerical section
              durationMinutes: 180, totalMarks: 70,
              sections: [
                { label: 'Part A', title: 'MCQ & Fill-in (1 mark)', questionType: 'mcq_single', count: 20, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Part B', title: 'Short Answer (2 marks)', questionType: 'short_answer', count: 9, marksPerQuestion: 2, negativeMarks: 0, attemptLimit: 5 },
                { label: 'Part C', title: 'Short Answer (3 marks)', questionType: 'short_answer', count: 9, marksPerQuestion: 3, negativeMarks: 0, attemptLimit: 5 },
                { label: 'Part D', title: 'Long Answer (5 marks)', questionType: 'long_answer', count: 6, marksPerQuestion: 5, negativeMarks: 0, attemptLimit: 3 },
                { label: 'Part E', title: 'Numerical / Application (5 marks)', questionType: 'numerical', count: 4, marksPerQuestion: 5, negativeMarks: 0, attemptLimit: 2 },
              ],
            },
            'Uttar Pradesh': {
              // UP Board 12th: 70m theory + 30m practicals; 5-section structure
              durationMinutes: 180, totalMarks: 70,
              sections: [
                { label: 'Section A', title: 'Objective MCQ (1 mark)', questionType: 'mcq_single', count: 7, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Section B', title: 'Very Short Answer (1 mark)', questionType: 'short_answer', count: 8, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Section C', title: 'Short Answer (2 marks)', questionType: 'short_answer', count: 6, marksPerQuestion: 2, negativeMarks: 0 },
                { label: 'Section D', title: 'Short Answer (3 marks)', questionType: 'short_answer', count: 6, marksPerQuestion: 3, negativeMarks: 0 },
                { label: 'Section E', title: 'Long Answer (5 marks)', questionType: 'long_answer', count: 5, marksPerQuestion: 5, negativeMarks: 0 },
              ],
            },
            'Gujarat': {
              // GSEB HSC: 100m total theory (no separate practicals); 50% OMR + 50% descriptive
              durationMinutes: 180, totalMarks: 100,
              sections: [
                { label: 'Part A', title: 'OMR MCQ (1 mark)', questionType: 'mcq_single', count: 50, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Part B-I', title: 'Short Answer (2 marks)', questionType: 'short_answer', count: 8, marksPerQuestion: 2, negativeMarks: 0 },
                { label: 'Part B-II', title: 'Medium Answer (3 marks)', questionType: 'short_answer', count: 6, marksPerQuestion: 3, negativeMarks: 0 },
                { label: 'Part B-III', title: 'Long Answer (4 marks)', questionType: 'long_answer', count: 4, marksPerQuestion: 4, negativeMarks: 0 },
              ],
            },
            'West Bengal': {
              // HS: 70m theory + 30m practicals; semester-based (Sem3: 35m MCQ, Sem4: 35m SA+LA)
              durationMinutes: 180, totalMarks: 70,
              sections: [
                { label: 'Sem 3', title: 'MCQ (1 mark)', questionType: 'mcq_single', count: 35, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Sem 4', title: 'Short Answer (2 marks)', questionType: 'short_answer', count: 5, marksPerQuestion: 2, negativeMarks: 0 },
                { label: 'Sem 4', title: 'Long Answer (5 marks)', questionType: 'long_answer', count: 5, marksPerQuestion: 5, negativeMarks: 0 },
              ],
            },
            'Rajasthan': {
              // RBSE Class 12 Science: 56m theory + 30m practicals + 14m internal; fractional marks
              durationMinutes: 180, totalMarks: 56,
              sections: [
                { label: 'Section A', title: 'MCQ (½ mark each)', questionType: 'mcq_single', count: 18, marksPerQuestion: 0.5, negativeMarks: 0 },
                { label: 'Section A', title: 'Fill in the Blanks (½ mark each)', questionType: 'short_answer', count: 10, marksPerQuestion: 0.5, negativeMarks: 0 },
                { label: 'Section A', title: 'Very Short Answer (1 mark)', questionType: 'short_answer', count: 10, marksPerQuestion: 1, negativeMarks: 0 },
                { label: 'Section B', title: 'Short Answer (1.5 marks)', questionType: 'short_answer', count: 10, marksPerQuestion: 1.5, negativeMarks: 0 },
                { label: 'Section C', title: 'Short Answer (3 marks)', questionType: 'short_answer', count: 3, marksPerQuestion: 3, negativeMarks: 0 },
                { label: 'Section D', title: 'Long Answer (4 marks)', questionType: 'long_answer', count: 2, marksPerQuestion: 4, negativeMarks: 0 },
              ],
            },
            'Andhra Pradesh': {
              // AP Inter (BIEAP): 60m theory + 40m practicals; A compulsory, B+C have internal choice
              durationMinutes: 180, totalMarks: 60,
              sections: [
                { label: 'Section A', title: 'Very Short Answer (2 marks)', questionType: 'short_answer', count: 10, marksPerQuestion: 2, negativeMarks: 0 },
                { label: 'Section B', title: 'Short Answer (4 marks)', questionType: 'short_answer', count: 8, marksPerQuestion: 4, negativeMarks: 0, attemptLimit: 6 },
                { label: 'Section C', title: 'Long Answer (8 marks)', questionType: 'long_answer', count: 3, marksPerQuestion: 8, negativeMarks: 0, attemptLimit: 2 },
              ],
            },
            'Telangana': {
              // TS Inter (TSBIE): 60m theory + 30m practicals; same section pattern as AP Inter
              durationMinutes: 180, totalMarks: 60,
              sections: [
                { label: 'Section A', title: 'Very Short Answer (2 marks)', questionType: 'short_answer', count: 10, marksPerQuestion: 2, negativeMarks: 0 },
                { label: 'Section B', title: 'Short Answer (4 marks)', questionType: 'short_answer', count: 8, marksPerQuestion: 4, negativeMarks: 0, attemptLimit: 6 },
                { label: 'Section C', title: 'Long Answer (8 marks)', questionType: 'long_answer', count: 3, marksPerQuestion: 8, negativeMarks: 0, attemptLimit: 2 },
              ],
            },
          },
        },
        {
          name: 'A-Levels',
          description: 'Cambridge / Edexcel A-Level — 60 marks / 90 min (Paper 1 + Paper 2 combined simulation)',
          grades: [11, 12],
          subjects: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'Economics', 'Business Studies', 'Accountancy'],
          boards: ['Cambridge'],
          blueprint: {
            durationMinutes: 90,
            totalMarks: 60,
            sections: [
              { label: 'Section A', title: 'Multiple Choice (Paper 1 style)', questionType: 'mcq_single', count: 30, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Structured Response (Paper 2 style)', questionType: 'short_answer', count: 6, marksPerQuestion: 5, negativeMarks: 0 },
            ],
          },
        },
        {
          name: 'IB DP',
          description: 'International Baccalaureate Diploma Programme — 60 marks / 90 min',
          grades: [11, 12],
          subjects: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'Economics', 'Business Studies', 'Accountancy'],
          boards: ['IB'],
          blueprint: {
            durationMinutes: 90,
            totalMarks: 60,
            sections: [
              { label: 'Section A', title: 'Multiple Choice', questionType: 'mcq_single', count: 20, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Short Answer', questionType: 'short_answer', count: 5, marksPerQuestion: 4, negativeMarks: 0 },
              { label: 'Section C', title: 'Extended Response', questionType: 'long_answer', count: 2, marksPerQuestion: 10, negativeMarks: 0 },
            ],
          },
        },
      ],
    },
  },
  AU: {
    region: 'AU',
    countryCode: 'AU',
    configVersion: 2,
    features: {
      showRealLifeExamples: false,
      showChatbot: true,
      showWriting: false,
      showBadges: true,
      showExamPrep: true,
      showLegacyConceptSections: false,
      showExplorePanel: true,
    },
    curriculum: {
      boardOptions: ['Australian Curriculum'],
      gradeRange: { min: 10, max: 12 },
      availableExams: ['HSC', 'VCE', 'ATAR'],
    },
    locale: {
      currency: 'AUD',
      currencySymbol: '$',
      names: ['Oliver', 'Sophie', 'Noah', 'Charlotte', 'Liam'],
      places: ['Sydney', 'Melbourne', 'Perth', 'Brisbane', 'Adelaide'],
      timezone: 'Australia/Sydney',
      spellingStyle: 'en-AU',
    },
    chatbot: {
      botName: 'Chinni',
      maxDoubtsPerSession: 5,
    },
    practiceTest: {
      exams: [
        {
          name: 'HSC',
          description: 'Higher School Certificate (NSW) — 100 marks / 3 hrs',
          grades: [11, 12],
          subjects: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English'],
          blueprint: {
            durationMinutes: 180,
            totalMarks: 100,
            sections: [
              { label: 'Section A', title: 'Multiple Choice', questionType: 'mcq_single', count: 20, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Short Answer', questionType: 'short_answer', count: 5, marksPerQuestion: 4, negativeMarks: 0 },
              { label: 'Section C', title: 'Extended Response', questionType: 'long_answer', count: 6, marksPerQuestion: 10, negativeMarks: 0 },
            ],
          },
        },
        {
          name: 'VCE',
          description: 'Victorian Certificate of Education — 120 marks / 2.5 hrs',
          grades: [11, 12],
          subjects: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English'],
          blueprint: {
            durationMinutes: 150,
            totalMarks: 120,
            sections: [
              { label: 'Section A', title: 'Multiple Choice', questionType: 'mcq_single', count: 20, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Short Answer', questionType: 'short_answer', count: 8, marksPerQuestion: 5, negativeMarks: 0 },
              { label: 'Section C', title: 'Extended Response', questionType: 'long_answer', count: 4, marksPerQuestion: 15, negativeMarks: 0 },
            ],
          },
        },
        {
          name: 'ATAR',
          description: 'Australian Tertiary Admission Rank Exams (WA) — 120 marks / 3 hrs',
          grades: [12],
          subjects: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English'],
          blueprint: {
            durationMinutes: 180,
            totalMarks: 120,
            sections: [
              { label: 'Section A', title: 'Multiple Choice', questionType: 'mcq_single', count: 30, marksPerQuestion: 1, negativeMarks: 0 },
              { label: 'Section B', title: 'Short Answer', questionType: 'short_answer', count: 6, marksPerQuestion: 5, negativeMarks: 0 },
              { label: 'Section C', title: 'Extended Response', questionType: 'long_answer', count: 4, marksPerQuestion: 15, negativeMarks: 0 },
            ],
          },
        },
      ],
    },
  },
};

// ── Getters ────────────────────────────────────────────────────

function detectRegion(): string {
  return import.meta.env.VITE_REGION || 'IN';
}

let _cachedRegion: string | null = null;

export function getRegion(): string {
  if (!_cachedRegion) {
    _cachedRegion = detectRegion();
  }
  return _cachedRegion;
}

export function getRegionConfig(): RegionConfig {
  const region = getRegion();
  return REGION_CONFIGS[region] || REGION_CONFIGS['IN'];
}

/**
 * Override region at runtime (e.g. from user profile).
 * Call early in app lifecycle if needed.
 */
export function setRegion(region: string): void {
  _cachedRegion = region;
}

// ── Convenience helpers ────────────────────────────────────────

export function isFeatureEnabled(feature: keyof RegionFeatures): boolean {
  return getRegionConfig().features[feature];
}

export function getCurriculumConfig(): RegionCurriculum {
  return getRegionConfig().curriculum;
}

export function getLocaleConfig(): RegionLocale {
  return getRegionConfig().locale;
}

export function getChatbotConfig(): RegionConfig['chatbot'] {
  return getRegionConfig().chatbot;
}


export function getAvailableExams(): string[] {
  return getRegionConfig().curriculum.availableExams;
}

// ── Practice Test helpers ────────────────────────────────────

/** Get exams filtered by grade AND board. Universal exams (no boards field) always show. */
export function getExamsForStudent(grade: number, board?: string): ExamConfig[] {
  return getRegionConfig().practiceTest.exams.filter(e =>
    e.grades.includes(grade) &&
    (!e.boards || !board || e.boards.includes(board))
  );
}

/** Legacy: get exams by grade only (no board filter) */
export function getExamsForGrade(grade: number): ExamConfig[] {
  return getRegionConfig().practiceTest.exams.filter(e => e.grades.includes(grade));
}

export function getExamSubjects(examName: string): string[] {
  const exam = getRegionConfig().practiceTest.exams.find(e => e.name === examName);
  return exam?.subjects || [];
}

/**
 * Returns the blueprint for an exam.
 * Pass `lookupKey` to get a subject-specific blueprint (SAT: subject name)
 * or a state-specific blueprint (State Board: state name).
 */
export function getExamBlueprint(examName: string, lookupKey?: string): PaperBlueprint | null {
  const exam = getRegionConfig().practiceTest.exams.find(e => e.name === examName);
  if (!exam) return null;
  if (lookupKey && exam.subjectBlueprints?.[lookupKey]) return exam.subjectBlueprints[lookupKey];
  return exam.blueprint;
}

export function getExam(examName: string): ExamConfig | null {
  return getRegionConfig().practiceTest.exams.find(e => e.name === examName) ?? null;
}

export function getPracticeTestExams(): ExamConfig[] {
  return getRegionConfig().practiceTest.exams;
}
